<template>
    <div>
        <div class="d-flex flex-row flex-wrap align-items-end ratings-header justify-content-between mb-3">
            <div class="mt-4 w-100 w-md-25">
                <button
                    type="button"
                    class="btn btn-primary w-100 w-md-auto mb-3 mb-md-0 text-nowrap"
                    @click="useModal().openModal('ProductReviewModal', ProductReviewModal)"
                >{{ t('products.write_review') }}</button>
            </div>
            <div v-if="ratingsData.data.length" class="d-flex align-items-center sort-inline ms-auto">
                <label class="form-label mb-0 me-2 nowrap">{{ t('products.sort_by') }}</label>
                <CountryIcon :country="localeStore.prefix" :language="true"/>
                <select name="sort" class="form-select w-auto" @change="currentSort = $event.target.value">
                    <option
                        v-for="(name, value) in sortOptions"
                        :key="value"
                        :value="value"
                        :selected="value === currentSort"
                    >{{ name }}</option>
                </select>
            </div>
        </div>
        <div
            v-for="rating in ratingsData.data"
            :key="rating.id"
            class="rating-container d-flex flex-column mt-1 bg-grey-black p-3 pb-2 mb-3 rounded-1 font-size-14"
        >
            <div class="d-flex flex-row flex-wrap align-items-center justify-content-between mb-2">
                <div class="d-flex align-items-center me-2">
                    <div class="me-2">
                        <ProductRating
                            :prefix="'product_reviews_' + rating.id"
                            readonly
                            hide-review-link
                            hide-avg
                            :value="rating.value"
                        />
                    </div>
                    <div class="flag-container">
                        <CountryIcon v-if="rating.country" :country="rating.country" />
                    </div>
                    <span class="fw-bold">{{ rating.author }}</span>
                </div>
                <div class="d-flex align-items-center gap-2 small">
                    <strong class="ms-1">{{ rating.date }}</strong>
                </div>
            </div>
            <div class="content">
                <p class="mb-0 fw-bold">{{ rating.title}}</p>
                <p
                    class="mb-0 comment"
                    :class="{ expanded: expanded[rating.id] }"
                    :ref="el => { if (el) commentsRefs[rating.id] = el }"
                >{{ rating.comment }}</p>
                <button
                    v-if="showToggle[rating.id]"
                    class="btn btn-link p-0 see-toggle float-center w-100"
                    @click="toggle(rating.id)"
                >{{ expanded[rating.id] ? t('products.see_less') : t('products.see_more') }}</button>
            </div>
        </div>
        <div class="clearfix">
            <div class="float-start">
                <Pagination
                    v-model="page"
                    :value="page"
                    :total="ratingsData.last_page"
                    :onChange="loadRatings"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
const ProductRating = defineAsyncComponent(() => import('~/components/Product/ProductRating.vue'))
const Pagination = defineAsyncComponent(() => import('~/components/Pagination.vue'))
const CountryIcon = defineAsyncComponent(() => import('~/components/CountryIcon.vue'))
const ProductReviewModal = defineAsyncComponent(() => import('~/components/Modals/ProductReviewModal.vue'))
import { useProductPageStore } from '~/stores/ProductPageStore'
import { useLocaleStore } from '~/stores/LocaleStore'
import api from '~/apis/products'

const { t } = useI18n()
const store = useProductPageStore()
const localeStore = useLocaleStore();
const currentSort = ref('id DESC')
const sortOptions = ref({
    'creation_date DESC': t('products.sorting.newest'),
    'creation_date ASC': t('products.sorting.oldest'),
    'value DESC': t('products.sorting.highest'),
    'value ASC': t('products.sorting.lowest')
})
const page = ref(parseInt(useRoute().query.page || 1))
const ratingsData = ref({ data: [], last_page: 1 })
const expanded = reactive({})
const showToggle = reactive({})
const commentsRefs = reactive({})

const detectOverflow = () => {
    const maxLines = 2
    ratingsData.value.data.forEach(r => {
        const el = commentsRefs[r.id]
        if (!el) {
            showToggle[r.id] = false
            return
        }
        const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 16
        const maxHeight = lineHeight * maxLines
        showToggle[r.id] = el.scrollHeight > maxHeight + 1
    })
}

const loadRatings = async () => {
    const res = await api.ratings(store.product.id, currentSort.value, page.value, 3)
    ratingsData.value = res.data
    ratingsData.value.data.forEach(r => {
        expanded[r.id] = false
        showToggle[r.id] = false
    })
    Object.keys(commentsRefs).forEach(key => delete commentsRefs[key])
    await nextTick()
    detectOverflow()
}

watch(currentSort, loadRatings)

const toggle = id => {
    expanded[id] = !expanded[id]
}

defineExpose({loadRatings});
</script>

<style scoped>
.comment {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
    transition: max-height .3s ease;
}
.comment.expanded {
    -webkit-line-clamp: unset;
}
.sort-inline {
    white-space: nowrap;
}
.nowrap {
    white-space: nowrap;
}
.content {
    line-height: 1.5;
    min-height: 90px;
}
.flag-container {
    width: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.see-toggle {
    cursor: pointer;
    font-size: 0.9em;
    margin-left: 0.25rem;
    user-select: none;
}
</style>
